/**
 * Le impostazioni dell'apparecchio (03-SCHERMATE-TV §12).
 *
 * ## La regola che vale la pena avere qui
 *
 * «Risparmio risorse» è un **preset**: accendendolo spegne animazioni ed effetti, porta le immagini
 * a bassa qualità e la guida a tre ore. Ma se poi qualcuno riaccende le animazioni a mano, il preset
 * non è più attivo — e continuare a mostrarlo acceso sarebbe una bugia sullo stato
 * dell'apparecchio.
 *
 * È il genere di regola che, scritta dentro una schermata, diventa quattro `if` sparsi che al primo
 * campo nuovo se ne dimenticano uno. Qui è una funzione sola, e la prova la copre per intero.
 *
 * ## Perché il preset non si limita a «scrivere i valori»
 *
 * Perché dev'essere **reversibile senza memoria**: spegnerlo non rimette i valori di prima — non
 * sappiamo quali fossero, e inventarli sarebbe peggio — lascia semplicemente le cose come stanno e
 * smette di dichiararsi attivo. Chi vuole tornare indietro cambia le voci che gli interessano, che
 * è anche l'unica cosa che può volere davvero.
 */

import { TEXT_LOCALES, type TextLocale } from './localizedText.js';

export type ImageQuality = 'low' | 'medium' | 'high';
export type GuideHours = 3 | 6 | 24;

export interface PlayerSettings {
  /** Il preset. Non è un valore in più: è la **conseguenza** degli altri quattro. */
  readonly resourceSaving: boolean;
  readonly animations: boolean;
  readonly visualEffects: boolean;
  readonly imageQuality: ImageQuality;
  readonly guideHours: GuideHours;
  /** Il PIN per i contenuti per adulti: un interruttore, non una scala d'età (D-6). */
  readonly adultPinEnabled: boolean;
  /**
   * La lingua scelta a mano, o `'auto'` per quella del sistema.
   *
   * ## Perché la voce serve, e non era mai stata fatta
   *
   * `i18n.ts` lo dichiarava dal primo giorno — «su un televisore la lingua di sistema è spesso
   * quella del paese in cui l'apparecchio è stato venduto, non quella di chi lo guarda» — e la voce
   * nelle impostazioni **non c'era**: la lingua si ricavava da `navigator.languages` e non si poteva
   * cambiare. Un televisore comprato in Germania da chi parla italiano restava in tedesco.
   *
   * ## `'auto'` è un valore, non un segnaposto per «niente»
   *
   * È il predefinito, ed è diverso dallo scegliere l'italiano: con `'auto'` la lingua **segue** il
   * sistema, quindi cambia da sé se qualcuno cambia quella del televisore. Memorizzare `'it'` di
   * default avrebbe congelato la lingua del primo avvio per sempre.
   */
  readonly locale: TextLocale | 'auto';
}

/** I valori che «Risparmio risorse» impone quando lo si accende. */
export const SAVING_PRESET = {
  animations: false,
  visualEffects: false,
  imageQuality: 'low',
  guideHours: 3,
} as const satisfies Pick<
  PlayerSettings,
  'animations' | 'visualEffects' | 'imageQuality' | 'guideHours'
>;

export const DEFAULT_SETTINGS: PlayerSettings = {
  resourceSaving: false,
  // La lingua del sistema, finché nessuno ne sceglie una: vedi `locale` qui sopra.
  locale: 'auto',
  animations: true,
  visualEffects: true,
  imageQuality: 'medium',
  guideHours: 6,
  adultPinEnabled: false,
};

/** Le quattro voci che il preset governa. Elencarle una volta sola è il punto di tutto il modulo. */
const PRESET_KEYS = ['animations', 'visualEffects', 'imageQuality', 'guideHours'] as const;

/** `true` se i quattro valori coincidono con quelli del preset. */
export function matchesPreset(settings: PlayerSettings): boolean {
  return PRESET_KEYS.every((key) => settings[key] === SAVING_PRESET[key]);
}

/**
 * Cambia una voce, e aggiorna il preset di conseguenza.
 *
 * Tre comportamenti, e il terzo è quello che si dimentica:
 *
 * 1. accendere il preset **impone** i quattro valori;
 * 2. toccare una delle quattro a mano **spegne** il preset;
 * 3. …a meno che il valore scelto sia proprio quello del preset e gli altri tre già lo siano — nel
 *    qual caso il preset si **riaccende** da solo, perché descrive esattamente lo stato in cui si
 *    è. Un preset che resta spento mentre tutte le sue voci sono al suo valore è la stessa bugia
 *    del punto 2, al contrario.
 */
export function changeSetting<K extends keyof PlayerSettings>(
  settings: PlayerSettings,
  key: K,
  value: PlayerSettings[K],
): PlayerSettings {
  if (key === 'resourceSaving') {
    // Spegnerlo non rimette niente: non sappiamo quali fossero i valori di prima.
    if (value === false) return { ...settings, resourceSaving: false };
    return { ...settings, ...SAVING_PRESET, resourceSaving: true };
  }

  const next = { ...settings, [key]: value };

  if ((PRESET_KEYS as ReadonlyArray<string>).includes(key as string)) {
    return { ...next, resourceSaving: matchesPreset(next) };
  }

  // Le voci fuori dal preset — il PIN — non lo toccano.
  return next;
}

/**
 * Le impostazioni lette da `line.app_data`, che è un oggetto libero.
 *
 * Arriva da un archivio che altri apparecchi scrivono e che una versione futura dell'app potrebbe
 * riempire diversamente: **niente di ciò che c'è dentro è garantito**. Ogni campo si verifica uno
 * per uno e quello che non si riconosce torna al valore predefinito, invece di entrare così com'è e
 * far sbagliare un confronto tre schermate più in là.
 */
export function parseSettings(raw: unknown): PlayerSettings {
  const source = (raw ?? {}) as Record<string, unknown>;
  const boolean = (key: keyof PlayerSettings, fallback: boolean): boolean =>
    typeof source[key] === 'boolean' ? (source[key] as boolean) : fallback;

  const quality = source.imageQuality;
  const hours = source.guideHours;
  /*
    La lingua si verifica contro l'elenco, come tutto il resto.

    Un `locale` sconosciuto — una lingua che una versione futura dell'app avesse aggiunto, o un
    valore scritto a mano — tornerebbe `'auto'`, e non entrerebbe così com'è a far cercare un file di
    traduzioni che non esiste. L'app, con messaggi mai caricati, resterebbe sulla schermata d'avvio.
  */
  const locale = source.locale;

  const settings: PlayerSettings = {
    resourceSaving: boolean('resourceSaving', DEFAULT_SETTINGS.resourceSaving),
    animations: boolean('animations', DEFAULT_SETTINGS.animations),
    visualEffects: boolean('visualEffects', DEFAULT_SETTINGS.visualEffects),
    imageQuality:
      quality === 'low' || quality === 'medium' || quality === 'high'
        ? quality
        : DEFAULT_SETTINGS.imageQuality,
    guideHours: hours === 3 || hours === 6 || hours === 24 ? hours : DEFAULT_SETTINGS.guideHours,
    adultPinEnabled: boolean('adultPinEnabled', DEFAULT_SETTINGS.adultPinEnabled),
    locale:
      locale === 'auto' || (TEXT_LOCALES as ReadonlyArray<unknown>).includes(locale)
        ? (locale as TextLocale | 'auto')
        : DEFAULT_SETTINGS.locale,
  };

  /*
    E il preset si **ricalcola**, non si legge.

    Un archivio può contenere `resourceSaving: true` accanto a valori che non sono quelli del
    preset: l'ha scritto una versione più vecchia, o un apparecchio che ha cambiato una voce senza
    aggiornarlo. Fidarsi del campo salvato significherebbe mostrare un preset acceso su
    un'impostazione che non lo è — la bugia che questo modulo esiste per non avere.
  */
  return { ...settings, resourceSaving: matchesPreset(settings) };
}
